import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../config';
import { formatDateToDDMMYYYY, getTodayYYYYMMDD } from '../../utils/dateUtils';
import './AdminDailyCashbook.css';

const FINANCE_NAME = 'M/S SHREE MAHAKAL FINANCE AND LEASING (R)';
const FINANCE_ADDRESS = 'Khajuri, Aland , Kalaburgi';

const ACCOUNT_GROUP_ORDER = [
  'General Loan A/C',
  'CD Loan A/C',
  'TBD Loan A/C',
  'General Loan EMI A/C',
  'CD Loan EMI A/C',
  'GLInterest A/c',
  'Cdinterest A/C',
  'TBDInterest A/C',
  'SB A/C',
];

const splitAmountByMode = (amount, paymentMode) => {
  const value = Number(amount || 0);
  const isCash = String(paymentMode || 'Cash').toLowerCase() === 'cash';
  return {
    cash: isCash ? value : 0,
    transfer: isCash ? 0 : value,
    total: value,
  };
};

const formatStatementAmount = (value, showZero = false) => {
  const amount = Number(value || 0);
  if (!showZero && amount === 0) return '-';
  return amount.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const amountClass = (value) => (Number(value || 0) < 0 ? 'negative-amount' : '');

const formatPrintDate = (value) => {
  if (!value) return '';
  const datePart = String(value).slice(0, 10);
  return formatDateToDDMMYYYY(datePart);
};

const DAYBOOK_PRINT_STYLES = `
  @page {
    size: A4 portrait;
    margin: 10mm 8mm 12mm;
  }
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    width: 100%;
    color: #000;
    background: #fff;
    font-family: 'Times New Roman', Times, serif;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .daybook-statement {
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    padding: 0;
  }
  .daybook-header {
    text-align: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #333;
  }
  .daybook-header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12pt;
    margin-bottom: 8px;
    color: #111;
  }
  .page-info, .daybook-date {
    font-family: 'Courier New', Courier, monospace;
    font-size: 12pt;
  }
  .company-name {
    margin: 0;
    color: #8b0000;
    font-size: 20pt;
    font-weight: 800;
    letter-spacing: 0.3px;
    line-height: 1.25;
  }
  .company-address {
    margin: 5px 0 10px;
    font-size: 12pt;
    color: #111;
  }
  .report-title {
    margin: 0;
    color: #0a2f6b;
    font-size: 16pt;
    font-weight: 800;
    letter-spacing: 0.4px;
  }
  .daybook-table {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #000;
    table-layout: fixed;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 12pt;
  }
  .daybook-table th,
  .daybook-table td {
    border: 1px solid #444;
    padding: 6px 5px;
    line-height: 1.4;
    vertical-align: middle;
    word-wrap: break-word;
  }
  .daybook-table th {
    text-align: center;
    font-weight: 700;
    background: #f3f4f6;
    color: #000;
    border-bottom: 1.5px solid #000;
    font-size: 11pt;
    padding: 8px 4px;
  }
  .particulars-col { width: 24%; }
  .vno-col { width: 6%; }
  .amount-col {
    width: calc(8.5% + 0.5cm);
    text-align: right;
    font-family: 'Courier New', Courier, monospace;
    font-size: 12pt;
    font-weight: 600;
    white-space: nowrap;
    padding-left: 3px;
    padding-right: 4px;
  }
  .particulars-cell {
    text-align: left;
    font-size: 12px;
    line-height: 1.3;
    white-space: normal;
  }
  .vno-cell {
    text-align: center;
    font-family: 'Courier New', Courier, monospace;
    font-size: 12pt;
    font-weight: 600;
  }
  .account-group-row td {
    color: #8b0000;
    font-weight: 700;
    font-size: 12px;
    padding: 6px 8px;
    background: #fffaf5;
    border-top: 1.5px solid #666;
  }
  .summary-row td { font-weight: 700; }
  .total-row td {
    border-top: 2px solid #000;
    background: #f3f4f6;
    padding: 8px;
  }
  .balance-block {
    padding: 10px 12px;
    vertical-align: middle;
  }
  .balance-block-label {
    display: block;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 10pt;
    font-weight: 700;
    color: #222;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 5px;
  }
  .balance-block-value {
    display: block;
    font-family: 'Courier New', Courier, monospace;
    font-size: 13pt;
    font-weight: 800;
    text-align: right;
    color: #000;
  }
  .balance-block-value.negative-amount,
  .amount-col.negative-amount {
    color: #8b0000;
  }
  .credit-balance-block {
    background: #f8fafc;
    border-left: 2px solid #0a2f6b;
  }
  .debit-balance-block {
    background: #f8fafc;
    border-left: 2px solid #8b0000;
  }
  .grand-block {
    background: #eef2ff;
    border-top: 1.5px solid #000;
  }
  .final-grand-row td { border-bottom: 1.5px solid #000; }
  .no-data { text-align: center; padding: 20px; font-style: italic; font-size: 12pt; }
  .daybook-footer {
    margin-top: 12px;
    padding-top: 10px;
    text-align: center;
    font-size: 12pt;
    border-top: 1px dashed #888;
  }
  .closing-balance-line { margin: 0 0 6px; color: #111; font-size: 12pt; }
  .closing-balance-line strong {
    font-family: 'Courier New', Courier, monospace;
    font-size: 13pt;
    color: #0a2f6b;
  }
  .closing-balance-line strong.negative-amount { color: #8b0000; }
  .end-statement {
    margin: 0;
    font-size: 11pt;
    letter-spacing: 1.2px;
    color: #444;
  }
`;

const buildParticulars = (entry) => {
  const prefix = entry.side === 'credit' ? 'By.' : 'To.';
  const mode = entry.payment_mode || 'Cash';
  const accountPart = entry.account_no ? `${entry.account_no} ` : '';
  const name = String(entry.party_name || '').trim() || 'N/A';
  return `${prefix} ${accountPart}${mode} ${name}`.replace(/\s+/g, ' ').trim();
};

const AdminDailyCashbook = () => {
  const [dateFrom, setDateFrom] = useState(getTodayYYYYMMDD());
  const [dateTo, setDateTo] = useState(getTodayYYYYMMDD());
  const [cashbooks, setCashbooks] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchCashbook = useCallback(async () => {
    if (!dateFrom || !dateTo) {
      setError('Please select both From Date and To Date.');
      return;
    }
    if (dateFrom > dateTo) {
      setError('From Date cannot be greater than To Date.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const qs = `date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}`;
      const response = await fetch(
        `${API_BASE_URL}/api/admin/daily-cashbook-range?${qs}`
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || payload.message || 'Failed to fetch cashbook.');
      }
      const data = await response.json();
      const rows = Array.isArray(data.daybooks) ? data.daybooks : [];
      setCashbooks(rows);
      setActiveIndex(0);
    } catch (err) {
      console.error('Admin cashbook fetch failed:', err);
      setError(err.message || 'Unexpected error while loading cashbook.');
      setCashbooks([]);
      setActiveIndex(0);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchCashbook();
  }, [fetchCashbook]);

  const activeCashbook = cashbooks[activeIndex] || null;

  const groupedRows = useMemo(() => {
    const entries = activeCashbook?.entries || [];
    const groups = new Map();

    entries.forEach((entry) => {
      const key = entry.accountGroup || 'Other';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(entry);
    });

    const orderedKeys = [
      ...ACCOUNT_GROUP_ORDER.filter((key) => groups.has(key)),
      ...Array.from(groups.keys()).filter((key) => !ACCOUNT_GROUP_ORDER.includes(key)),
    ];

    return orderedKeys.map((accountGroup) => ({
      accountGroup,
      entries: groups.get(accountGroup) || [],
    }));
  }, [activeCashbook]);

  const totals = activeCashbook?.totals || {
    totalDebit: 0,
    totalCredit: 0,
    totalCreditCash: 0,
    totalCreditTransfer: 0,
    totalDebitCash: 0,
    totalDebitTransfer: 0,
    creditGrandTotal: 0,
    debitGrandTotal: 0,
  };

  const openingBalance = Number(activeCashbook?.openingBalance || 0);
  const closingBalance = Number(activeCashbook?.closingBalance || 0);
  const creditGrandTotal = totals.creditGrandTotal ?? openingBalance + totals.totalCredit;
  const debitGrandTotal = totals.debitGrandTotal ?? closingBalance + totals.totalDebit;
  const displayDate = formatPrintDate(activeCashbook?.date || dateFrom);

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const renderAmountCell = (side, entrySide, amounts, field) => {
    if (entrySide !== side) return '-';
    return formatStatementAmount(amounts[field]);
  };

  const buildPrintTableRows = (rows, statementDate) => {
    if (rows.length === 0) {
      return `<tr><td colspan="8" class="no-data">No transactions found for ${escapeHtml(statementDate)}</td></tr>`;
    }

    let html = '';
    rows.forEach((group) => {
      html += `<tr class="account-group-row"><td colspan="8">${escapeHtml(group.accountGroup)}</td></tr>`;
      group.entries.forEach((entry) => {
        const amounts = splitAmountByMode(entry.amount, entry.payment_mode);
        const creditAmounts = entry.side === 'credit' ? amounts : { cash: 0, transfer: 0, total: 0 };
        const debitAmounts = entry.side === 'debit' ? amounts : { cash: 0, transfer: 0, total: 0 };
        html += `
          <tr>
            <td class="particulars-cell">${escapeHtml(buildParticulars(entry))}</td>
            <td class="vno-cell">${escapeHtml(entry.vNo || '-')}</td>
            <td class="amount-col">${renderAmountCell('credit', entry.side, creditAmounts, 'cash')}</td>
            <td class="amount-col">${renderAmountCell('credit', entry.side, creditAmounts, 'transfer')}</td>
            <td class="amount-col">${renderAmountCell('credit', entry.side, creditAmounts, 'total')}</td>
            <td class="amount-col">${renderAmountCell('debit', entry.side, debitAmounts, 'cash')}</td>
            <td class="amount-col">${renderAmountCell('debit', entry.side, debitAmounts, 'transfer')}</td>
            <td class="amount-col">${renderAmountCell('debit', entry.side, debitAmounts, 'total')}</td>
          </tr>`;
      });
    });
    return html;
  };

  const buildGroupedRowsForCashbook = (daybook) => {
    const entries = daybook?.entries || [];
    const groups = new Map();
    entries.forEach((entry) => {
      const key = entry.accountGroup || 'Other';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(entry);
    });
    const orderedKeys = [
      ...ACCOUNT_GROUP_ORDER.filter((key) => groups.has(key)),
      ...Array.from(groups.keys()).filter((key) => !ACCOUNT_GROUP_ORDER.includes(key)),
    ];
    return orderedKeys.map((accountGroup) => ({
      accountGroup,
      entries: groups.get(accountGroup) || [],
    }));
  };

  const renderPrintStatement = (daybook, index, totalPages) => {
    const statementDate = formatPrintDate(daybook?.date);
    const statementTotals = daybook?.totals || totals;
    const statementOpening = Number(daybook?.openingBalance || 0);
    const statementClosing = Number(daybook?.closingBalance || 0);
    const statementCreditGrand =
      statementTotals.creditGrandTotal ?? statementOpening + statementTotals.totalCredit;
    const statementDebitGrand =
      statementTotals.debitGrandTotal ?? statementClosing + statementTotals.totalDebit;
    const statementRows = buildGroupedRowsForCashbook(daybook);
    const tableBody = buildPrintTableRows(statementRows, statementDate);

    return `
      <div class="daybook-statement print-page">
        <div class="daybook-header">
          <div class="daybook-header-top">
            <span class="page-info">Page ${index + 1} of ${totalPages}</span>
            <span class="daybook-date">Date : ${escapeHtml(statementDate)}</span>
          </div>
          <h1 class="company-name">${escapeHtml(FINANCE_NAME)}</h1>
          <p class="company-address">${escapeHtml(FINANCE_ADDRESS)}</p>
          <h2 class="report-title">Daily Cash &amp; Day Book</h2>
        </div>
        <table class="daybook-table">
          <thead>
            <tr>
              <th rowspan="2" class="particulars-col">Particulars</th>
              <th rowspan="2" class="vno-col">V No</th>
              <th colspan="3">Credit</th>
              <th colspan="3">Debit</th>
            </tr>
            <tr>
              <th class="amount-col">Cash</th>
              <th class="amount-col">Transfer</th>
              <th class="amount-col">Total</th>
              <th class="amount-col">Cash</th>
              <th class="amount-col">Transfer</th>
              <th class="amount-col">Total</th>
            </tr>
          </thead>
          <tbody>
            ${tableBody}
            <tr class="summary-row total-row">
              <td colspan="2">Total</td>
              <td class="amount-col">${formatStatementAmount(statementTotals.totalCreditCash, true)}</td>
              <td class="amount-col">${formatStatementAmount(statementTotals.totalCreditTransfer, true)}</td>
              <td class="amount-col">${formatStatementAmount(statementTotals.totalCredit, true)}</td>
              <td class="amount-col">${formatStatementAmount(statementTotals.totalDebitCash, true)}</td>
              <td class="amount-col">${formatStatementAmount(statementTotals.totalDebitTransfer, true)}</td>
              <td class="amount-col">${formatStatementAmount(statementTotals.totalDebit, true)}</td>
            </tr>
            <tr class="summary-row balance-summary-row">
              <td colspan="2"></td>
              <td colspan="3" class="balance-block credit-balance-block">
                <span class="balance-block-label">Opening Balance</span>
                <span class="balance-block-value">${formatStatementAmount(statementOpening, true)}</span>
              </td>
              <td colspan="3" class="balance-block debit-balance-block">
                <span class="balance-block-label">Closing Balance</span>
                <span class="balance-block-value">${formatStatementAmount(statementClosing, true)}</span>
              </td>
            </tr>
            <tr class="summary-row final-grand-row">
              <td colspan="2"></td>
              <td colspan="3" class="balance-block credit-balance-block grand-block">
                <span class="balance-block-label">Grand Total</span>
                <span class="balance-block-value">${formatStatementAmount(statementCreditGrand, true)}</span>
              </td>
              <td colspan="3" class="balance-block debit-balance-block grand-block">
                <span class="balance-block-label">Grand Total</span>
                <span class="balance-block-value">${formatStatementAmount(statementDebitGrand, true)}</span>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="daybook-footer">
          <p class="closing-balance-line">For the Day Closing Balance : <strong class="${amountClass(statementClosing)}">${formatStatementAmount(statementClosing, true)}</strong></p>
          <p class="end-statement">+++ END OF STATEMENT +++</p>
        </div>
      </div>
    `;
  };

  const handlePrint = () => {
    if (cashbooks.length === 0) return;

    const printWindow = window.open('', '_blank', 'width=1100,height=800');
    if (!printWindow) {
      alert('Please allow popups to print the day book.');
      return;
    }

    const pages = cashbooks
      .map((daybook, index) => renderPrintStatement(daybook, index, cashbooks.length))
      .join('\n');
    const printHtml = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Daily Cash &amp; Day Book (${escapeHtml(formatPrintDate(dateFrom))} to ${escapeHtml(
      formatPrintDate(dateTo)
    )})</title>
          <style>
            ${DAYBOOK_PRINT_STYLES}
            .print-page { page-break-after: always; }
            .print-page:last-child { page-break-after: auto; }
          </style>
        </head>
        <body>
          ${pages}
          <script>
            window.onload = function() {
              window.focus();
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          <\/script>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(printHtml);
    printWindow.document.close();
  };

  return (
    <div className="admin-cashbook-container">
      <div className="admin-cashbook-toolbar no-print">
        <div className="toolbar-title">
          <h2>Daily Cash &amp; Day Book</h2>
          <p>Select date range to view and print day books</p>
        </div>
        <div className="admin-cashbook-filter">
          <div className="filter-group">
            <label htmlFor="cashbook-date-from">From</label>
            <input
              id="cashbook-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="cashbook-date-to">To</label>
            <input
              id="cashbook-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          <button className="apply-button" onClick={fetchCashbook} disabled={loading}>
            {loading ? 'Loading…' : 'Apply'}
          </button>
          <button
            className="apply-button secondary"
            onClick={() => {
              const today = getTodayYYYYMMDD();
              setDateFrom(today);
              setDateTo(today);
            }}
            disabled={loading}
          >
            Today
          </button>
          <button className="apply-button print-btn" onClick={handlePrint} disabled={loading || cashbooks.length === 0}>
            Print
          </button>
        </div>
      </div>

      {error ? <div className="admin-cashbook-error no-print">{error}</div> : null}

      {loading && cashbooks.length === 0 ? (
        <div className="admin-cashbook-loading">Loading cashbook data…</div>
      ) : null}

      {cashbooks.length > 1 ? (
        <div className="admin-cashbook-filter no-print" style={{ marginBottom: '10px' }}>
          <div className="filter-group">
            <label htmlFor="cashbook-view-date">View Date</label>
            <select
              id="cashbook-view-date"
              value={activeIndex}
              onChange={(e) => setActiveIndex(Number(e.target.value))}
              disabled={loading}
            >
              {cashbooks.map((entry, index) => (
                <option key={entry.date} value={index}>
                  {formatPrintDate(entry.date)}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Total Days</label>
            <input value={cashbooks.length} readOnly />
          </div>
        </div>
      ) : null}

      {activeCashbook ? (
        <div className="daybook-statement">
          <div className="daybook-header">
            <div className="daybook-header-top">
              <span className="page-info">Page 1 of 1</span>
              <span className="daybook-date">Date : {displayDate}</span>
            </div>
            <h1 className="company-name">{FINANCE_NAME}</h1>
            <p className="company-address">{FINANCE_ADDRESS}</p>
            <h2 className="report-title">Daily Cash &amp; Day Book</h2>
          </div>

          <div className="admin-cashbook-table-wrapper">
            <table className="admin-cashbook-table daybook-table">
              <thead>
                <tr>
                  <th rowSpan="2" className="particulars-col">Particulars</th>
                  <th rowSpan="2" className="vno-col">V No</th>
                  <th colSpan="3" className="credit-header">Credit</th>
                  <th colSpan="3" className="debit-header">Debit</th>
                </tr>
                <tr>
                  <th className="amount-col">Cash</th>
                  <th className="amount-col">Transfer</th>
                  <th className="amount-col">Total</th>
                  <th className="amount-col">Cash</th>
                  <th className="amount-col">Transfer</th>
                  <th className="amount-col">Total</th>
                </tr>
              </thead>
              <tbody>
                {groupedRows.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="admin-cashbook-no-data">
                      No transactions found for {displayDate}
                    </td>
                  </tr>
                ) : (
                  groupedRows.map((group) => (
                    <React.Fragment key={group.accountGroup}>
                      <tr className="account-group-row">
                        <td colSpan="8">{group.accountGroup}</td>
                      </tr>
                      {group.entries.map((entry, index) => {
                        const amounts = splitAmountByMode(entry.amount, entry.payment_mode);
                        const creditAmounts =
                          entry.side === 'credit' ? amounts : { cash: 0, transfer: 0, total: 0 };
                        const debitAmounts =
                          entry.side === 'debit' ? amounts : { cash: 0, transfer: 0, total: 0 };

                        return (
                          <tr key={`${group.accountGroup}-${entry.vNo}-${index}`}>
                            <td className="particulars-cell">{buildParticulars(entry)}</td>
                            <td className="vno-cell">{entry.vNo || '-'}</td>
                            <td className="amount-col">
                              {entry.side === 'credit' ? formatStatementAmount(creditAmounts.cash) : '-'}
                            </td>
                            <td className="amount-col">
                              {entry.side === 'credit' ? formatStatementAmount(creditAmounts.transfer) : '-'}
                            </td>
                            <td className="amount-col">
                              {entry.side === 'credit' ? formatStatementAmount(creditAmounts.total) : '-'}
                            </td>
                            <td className="amount-col">
                              {entry.side === 'debit' ? formatStatementAmount(debitAmounts.cash) : '-'}
                            </td>
                            <td className="amount-col">
                              {entry.side === 'debit' ? formatStatementAmount(debitAmounts.transfer) : '-'}
                            </td>
                            <td className="amount-col">
                              {entry.side === 'debit' ? formatStatementAmount(debitAmounts.total) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))
                )}

                <tr className="summary-row total-row">
                  <td colSpan="2" className="summary-label">Total</td>
                  <td className="amount-col">{formatStatementAmount(totals.totalCreditCash, true)}</td>
                  <td className="amount-col">{formatStatementAmount(totals.totalCreditTransfer, true)}</td>
                  <td className="amount-col">{formatStatementAmount(totals.totalCredit, true)}</td>
                  <td className="amount-col">{formatStatementAmount(totals.totalDebitCash, true)}</td>
                  <td className="amount-col">{formatStatementAmount(totals.totalDebitTransfer, true)}</td>
                  <td className="amount-col">{formatStatementAmount(totals.totalDebit, true)}</td>
                </tr>

                <tr className="summary-row balance-summary-row">
                  <td colSpan="2" className="summary-spacer" />
                  <td colSpan="3" className="balance-block credit-balance-block">
                    <span className="balance-block-label">Opening Balance</span>
                    <span className={`balance-block-value ${amountClass(openingBalance)}`}>
                      {formatStatementAmount(openingBalance, true)}
                    </span>
                  </td>
                  <td colSpan="3" className="balance-block debit-balance-block">
                    <span className="balance-block-label">Closing Balance</span>
                    <span className={`balance-block-value ${amountClass(closingBalance)}`}>
                      {formatStatementAmount(closingBalance, true)}
                    </span>
                  </td>
                </tr>

                <tr className="summary-row final-grand-row">
                  <td colSpan="2" className="summary-spacer" />
                  <td colSpan="3" className="balance-block credit-balance-block grand-block">
                    <span className="balance-block-label">Grand Total</span>
                    <span className={`balance-block-value ${amountClass(creditGrandTotal)}`}>
                      {formatStatementAmount(creditGrandTotal, true)}
                    </span>
                  </td>
                  <td colSpan="3" className="balance-block debit-balance-block grand-block">
                    <span className="balance-block-label">Grand Total</span>
                    <span className={`balance-block-value ${amountClass(debitGrandTotal)}`}>
                      {formatStatementAmount(debitGrandTotal, true)}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="daybook-footer">
            <p className="closing-balance-line">
              For the Day Closing Balance :{' '}
              <strong className={amountClass(closingBalance)}>
                {formatStatementAmount(closingBalance, true)}
              </strong>
            </p>
            <p className="end-statement">+++ END OF STATEMENT +++</p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminDailyCashbook;
